// ─── Emotions classified by weight ───────────────────────────────────────────
const HEAVY = ["anxiety","anxious","sadness","sad","grief","fear","despair","hopeless","depressed","depression","suicidal","worthless"];
const MEDIUM = ["anger","angry","stress","stressed","overwhelm","overwhelmed","loneliness","lonely","frustrated","irritable","worried"];
const LIGHT  = ["calm","content","hopeful","grateful","relieved","happy","joy","peaceful","better","improving","proud","motivated"];

function classifyEmotion(emotion) {
  if (!emotion) return 0;
  const e = emotion.toLowerCase();
  if (LIGHT.some(k => e.includes(k)))  return 1;   // positive
  if (MEDIUM.some(k => e.includes(k))) return 0;   // neutral
  if (HEAVY.some(k => e.includes(k)))  return -1;  // heavy
  return 0;
}

// ─── Score a single session ───────────────────────────────────────────────────
function scoreSession(session) {
  const exchanges = session.exchanges || [];
  if (!exchanges.length) return 0;
  const total = exchanges.reduce((sum, ex) => sum + classifyEmotion(ex.emotion), 0);
  return total / exchanges.length; // -1 to 1
}

// Extract a calendar-day key from a session so repeats on the same day
// collapse into one — this is what actually stops same-day cramming from
// inflating growth. Prefers the real Firestore timestamp; falls back to the
// display string only if that's ever missing.
function getDayKey(session) {
  if (session.createdAt && typeof session.createdAt.toDate === "function") {
    const d = session.createdAt.toDate();
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }
  if (typeof session.startedAt === "string") {
    const datePart = session.startedAt.split(",")[0]; // e.g. "2 Jul 2026" from "2 Jul 2026, 10:00"
    if (datePart) return datePart.trim();
  }
  return session.id || `unknown-${Math.random()}`; // last-resort fallback, shouldn't normally trigger
}

// ─── Calculate overall tree stage from all sessions ───────────────────────────
// Returns 1-4. Growth is deliberately slow: a stage requires both an emotional
// trend AND a minimum number of DISTINCT DAYS active — not session count, so
// doing five sessions in one afternoon grows the tree the same as doing one.
// This is meant to feel like real, gradual progress — weeks, not a single sitting.
export function calcTreeStage(sessions, journeyCompleted = 0) {
  if (!sessions || sessions.length === 0) return 1;

  const uniqueDays = new Set(sessions.map(getDayKey)).size;

  // Weight recent sessions more heavily (this part is about emotional trend,
  // not volume, so session-level recency weighting is still the right tool)
  const scored = sessions.slice(0, 15).map((s, i) => {
    const weight = 1 / (i + 1); // most recent = weight 1, older = less
    return { score: scoreSession(s), weight };
  });

  const totalWeight = scored.reduce((sum, s) => sum + s.weight, 0);
  const weightedScore = scored.reduce((sum, s) => sum + s.score * s.weight, 0) / totalWeight;

  // Consistency bonus — now based on distinct days shown up, not total sessions
  const consistencyBonus = Math.min(uniqueDays / 40, 0.2); // up to +0.2, needs 40+ distinct days

  // Earned milestones nudge growth, but can't carry the tree alone
  const journeyBonus = Math.min(journeyCompleted * 0.02, 0.1); // up to +0.1, needs 5 milestones

  const final = weightedScore + consistencyBonus + journeyBonus;

  // Each stage needs BOTH the score AND enough DISTINCT DAYS — roughly:
  // stage 2 ≈ a week or two in, stage 3 ≈ 3-4 weeks, stage 4 ≈ 1.5-2 months
  if (final >= 0.5  && uniqueDays >= 18) return 4; // thriving
  if (final >= 0.25 && uniqueDays >= 8)  return 3; // growing stronger
  if (final >= -0.1 && uniqueDays >= 3)  return 2; // showing up
  return 1;                                          // just beginning
}

export const STAGE_LABELS = {
  1: { label: "Just beginning",    message: "Every journey starts with a single step. Your tree is taking root." },
  2: { label: "Showing up",        message: "You're here consistently. Your tree is starting to grow." },
  3: { label: "Growing stronger",  message: "Real progress is showing. New branches are forming." },
  4: { label: "Thriving",          message: "Your dedication is blooming. Look how far you've come." },
};
